/**
 * QR codes for payment by bank transfer (owner decision 2026-10-02: the only way customers pay):
 * the code staff show or send a customer, and the dialogs that make one — a wallet top-up (the
 * customer page), an order waiting for its transfer (the order page), an invoice's order (the
 * invoice page). The server (`POST /payment-requests`) makes the code with the receiving account
 * switched on in Cài đặt › Module › Thanh toán; the money is credited when a payment method reports it.
 */

import { QrCode } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api, isApiError } from '../../api';
import type { FundedFromWallet, NewPaymentRequestBody, PaymentRequestDetail } from '../../api/areas/commerce';
import { Callout, Field } from '../../components/blocks';
import { KeyValue } from '../../components/KeyValue';
import { CopyText } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { useAccess } from '../../lib/access';
import { formatDateTime, formatMoney } from '../../lib/format';
import { mayMakeCodes, wholeDong } from '../../lib/qrPayment';
import { ActionDialog, FormFooter, type DialogBodyProps } from '../operator/shared';
import { paymentPath } from './shared';

type Made = PaymentRequestDetail | FundedFromWallet;

const fundedFromWallet = (r: Made): r is FundedFromWallet => 'funded_from_wallet' in r;

/** An open request's code with what it carries; a note when there is no code any more. */
export function QrCard({ detail, link = true }: { detail: PaymentRequestDetail; link?: boolean }) {
  const { t } = useTranslation('commerce');
  const p = detail.request;
  const a = p.receiving_account;
  const content = p.content ?? p.code;
  if (!detail.qr) return <Callout tone="neutral">{t('payment.qr.closed')}</Callout>;
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
      <img src={detail.qr.svg_data_url} alt={t('qr.shown.image')} className="size-56 shrink-0 self-center rounded-md border bg-white p-2 sm:self-start" />
      <div className="min-w-0 flex-1 space-y-3">
        <KeyValue
          rows={[
            [t('qr.shown.bank'), a?.bank_name ?? a?.bank_bin ?? null],
            [t('qr.shown.account'), a ? <CopyText key="a" value={a.account_number} /> : null],
            [t('qr.shown.holder'), a?.account_name ?? null],
            [t('qr.shown.amount'), <span key="m" className="font-semibold tabular">{formatMoney({ amount: p.amount, currency: p.currency })}</span>],
            [t('qr.shown.content'), content ? <CopyText key="c" value={content} /> : null],
            [t('qr.shown.expires'), p.expires_at ? formatDateTime(p.expires_at) : null],
          ]}
        />
        <p className="text-xs text-muted-foreground">{t('qr.shown.contentHint')}</p>
        {link ? (
          <Link to={paymentPath(p.id)} className="text-sm font-medium text-primary underline-offset-4 hover:underline">
            {t('qr.shown.open')}
          </Link>
        ) : null}
      </div>
    </div>
  );
}

/** What a made request shows: its code, or that the wallet covered the order. */
function MadeView({ made, close }: { made: Made; close: () => void }) {
  const { t } = useTranslation('commerce');
  return (
    <div className="grid gap-4">
      {fundedFromWallet(made) ? <Callout tone="ok">{t('qr.order.funded')}</Callout> : <QrCard detail={made} />}
      <FormFooter busy={false} onClose={close} />
    </div>
  );
}

function TopUpForm({ customer, initial, close, changed }: DialogBodyProps & { customer: { id: string }; initial?: string }) {
  const { t } = useTranslation('commerce');
  const [amount, setAmount] = useState(initial ?? '');
  const w = useWrite((b: NewPaymentRequestBody, opts) => api.createPaymentRequest(b, opts));
  const value = wholeDong(amount);
  const wrong = amount.trim() !== '' && !value;
  if (w.state.phase === 'done') return <MadeView made={w.state.result} close={close} />;
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (value) void w.run({ customer_id: customer.id, amount: value }).then((r) => r && changed());
      }}
    >
      <Field label={t('qr.topUp.amount')} hint={wrong ? <span className="text-critical">{t('qr.topUp.amountInvalid')}</span> : t('qr.topUp.amountHint')}>
        <Input
          inputMode="numeric"
          autoComplete="off"
          value={amount}
          aria-invalid={wrong}
          onChange={(e) => {
            setAmount(e.target.value);
            if (w.state.phase === 'error') w.reset();
          }}
        />
      </Field>
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!value} submit={t('qr.topUp.submit')} onClose={close} />
    </form>
  );
}

/** "Mã QR nạp ví" for a customer: a code for the amount staff enter (`amount`: prefilled). */
export function TopUpQrAction({ customer, amount, label, onChanged }: { customer: { id: string; name: string }; amount?: string; label?: string; onChanged?: () => void }) {
  const { t } = useTranslation('commerce');
  const access = useAccess();
  if (!mayMakeCodes(access.grants, access.client)) return null;
  return (
    <ActionDialog label={label ?? t('qr.topUp.action')} icon={QrCode} title={t('qr.topUp.title')} description={t('qr.topUp.description', { customer: customer.name })} onChanged={onChanged}>
      {(p) => <TopUpForm customer={customer} initial={amount} {...p} />}
    </ActionDialog>
  );
}

/**
 * The code of an order waiting for its transfer. `fallback`: when the order does not wait (an
 * invoice's order is paid), a top-up code of this amount for the customer is offered instead.
 */
function OrderForm({ orderId, fallback, close, changed }: DialogBodyProps & { orderId: string; fallback?: { customer: { id: string }; amount: string } }) {
  const { t } = useTranslation('commerce');
  const w = useWrite((b: NewPaymentRequestBody, opts) => api.createPaymentRequest(b, opts));
  const [topUp, setTopUp] = useState(false);
  if (topUp && fallback) return <TopUpForm customer={fallback.customer} initial={fallback.amount} close={close} changed={changed} />;
  if (w.state.phase === 'done') return <MadeView made={w.state.result} close={close} />;
  const notAwaiting = w.state.phase === 'error' && isApiError(w.state.error) && w.state.error.code === 'ORDER_NOT_AWAITING_PAYMENT';
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void w.run({ order_id: orderId }).then((r) => r && changed());
      }}
    >
      {notAwaiting ? (
        <Callout tone="neutral">
          {fallback ? t('qr.invoice.notAwaiting') : t('qr.order.notAwaiting')}
          {fallback ? (
            <div className="mt-3">
              <Button type="button" size="sm" variant="outline" onClick={() => setTopUp(true)}>
                <QrCode className="size-4" aria-hidden="true" />
                {t('qr.invoice.topUp', { amount: formatMoney({ amount: fallback.amount, currency: 'VND' }) })}
              </Button>
            </div>
          ) : null}
        </Callout>
      ) : null}
      {w.state.phase === 'error' && !notAwaiting ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={notAwaiting} submit={t('qr.order.submit')} onClose={close} />
    </form>
  );
}

/** "Mã QR thanh toán" on an order waiting for its transfer. */
export function OrderQrAction({ orderId, onChanged }: { orderId: string; onChanged?: () => void }) {
  const { t } = useTranslation('commerce');
  const access = useAccess();
  if (!mayMakeCodes(access.grants, access.client)) return null;
  return (
    <ActionDialog label={t('qr.order.action')} icon={QrCode} title={t('qr.order.title')} description={t('qr.order.description')} onChanged={onChanged}>
      {(p) => <OrderForm orderId={orderId} {...p} />}
    </ActionDialog>
  );
}

/** "Mã QR thanh toán" on an invoice: its order's code, or a top-up code of the invoice total. */
export function InvoiceQrAction({ invoice }: { invoice: { order_id: string | null; customer_id: string; total: string; currency: string; document_type: string } }) {
  const { t } = useTranslation('commerce');
  const access = useAccess();
  if (!invoice.order_id || invoice.document_type !== 'INVOICE' || invoice.currency !== 'VND' || !mayMakeCodes(access.grants, access.client)) return null;
  const orderId = invoice.order_id;
  return (
    <ActionDialog label={t('qr.invoice.action')} icon={QrCode} title={t('qr.invoice.title')} description={t('qr.invoice.description')}>
      {(p) => <OrderForm orderId={orderId} fallback={{ customer: { id: invoice.customer_id }, amount: wholeDong(invoice.total) ?? invoice.total }} {...p} />}
    </ActionDialog>
  );
}
